import { Button, Flex, Stack, Text } from '@mantine/core';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OutstandingReason } from '../EidEasySigningModal.types';
import type { OutstandingDocument } from '../EidEasySigningModal.types';
import { StepBody } from './StepBody';
import { StepFooter } from './StepFooter';
import { StepHeading, StepSubHeading } from './typography';

interface Props {
	documents: OutstandingDocument[];
	onTryAgain: () => void;
}

const REASON_KEYS: Record<OutstandingReason, string> = {
	[OutstandingReason.ADVANCED_REQUIRED]:
		'eid_easy_signing.bulk_outstanding_reason.advanced_required',
	[OutstandingReason.WITNESS_REQUIRED]:
		'eid_easy_signing.bulk_outstanding_reason.witness_required',
	[OutstandingReason.DEFERRED]: 'eid_easy_signing.bulk_outstanding_reason.deferred',
	[OutstandingReason.NOTHING_TO_SIGN]: 'eid_easy_signing.bulk_outstanding_reason.nothing_to_sign',
	[OutstandingReason.PREPARE_FAILED]: 'eid_easy_signing.bulk_outstanding_reason.prepare_failed',
	[OutstandingReason.SIGN_FAILED]: 'eid_easy_signing.bulk_outstanding_reason.sign_failed',
	[OutstandingReason.SAVE_FAILED]: 'eid_easy_signing.bulk_outstanding_reason.save_failed',
	[OutstandingReason.NOT_RETURNED]: 'eid_easy_signing.bulk_outstanding_reason.not_returned',
	[OutstandingReason.UNCONFIRMED]: 'eid_easy_signing.bulk_outstanding_reason.unconfirmed',
};

/** Bulk signing result naming every selected document left unsigned, and why. */
export const OutstandingDocumentsStep = ({ documents, onTryAgain }: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const count = documents.length;

	return (
		<Flex direction="column" w="100%" h="100%">
			<StepBody>
				<StepHeading>{tCommonUi('eid_easy_signing.bulk_outstanding_title')}</StepHeading>
				<StepSubHeading>
					{count === 1
						? tCommonUi('eid_easy_signing.bulk_outstanding_description_single', {
								count,
							})
						: tCommonUi('eid_easy_signing.bulk_outstanding_description_other', {
								count,
							})}
				</StepSubHeading>
				<Stack gap="md">
					{documents.map((outstandingDocument) => (
						<Stack key={outstandingDocument.instanceId} gap={2}>
							<Text fw={600}>
								{outstandingDocument.templateName ??
									tCommonUi('eid_easy_signing.bulk_outstanding_untitled')}
							</Text>
							{outstandingDocument.investorNames.length > 0 && (
								<Text size="sm" c="dimmed">
									{outstandingDocument.investorNames.join(', ')}
								</Text>
							)}
							<Text size="sm" c="red">
								{tCommonUi(REASON_KEYS[outstandingDocument.reason])}
							</Text>
						</Stack>
					))}
				</Stack>
			</StepBody>
			<StepFooter>
				<Button variant="filled" fullWidth onClick={onTryAgain}>
					{tCommonUi('eid_easy_signing.try_again')}
				</Button>
			</StepFooter>
		</Flex>
	);
};
